import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon } from 'lucide-react';
import { categories, getCategory } from '../data/categories';
import { productsInCategory } from '../data/products';
import { ProductCard } from '../components/shop/ProductCard';
import { Button } from '../components/ui/Button';
import { CtaBand } from '../components/ui/CtaBand';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBrand } from '../contexts/BrandContext';
export function CategoryDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const category = slug ? getCategory(slug) : undefined;
  const {
    brand
  } = useBrand();
  useDocumentMeta({
    title: category ? `${category.name} — ${brand.name}` : `Shop — ${brand.name}`,
    description: category?.description ?? 'Doctor-prescribed treatments with the price in the open.',
    image: category?.image ?? brand.images.hero
  });
  if (!category) {
    return <main className="bg-cream" data-id="element-353" data-mp="3fz8s9fkvs23b">
        <div className="mx-auto max-w-shell px-6 py-32 md:px-8" data-id="element-354" data-mp="2i5o6ne30co1s">
          <h1 className="max-w-2xl font-serif text-4xl font-semibold leading-tight text-ink md:text-5xl" data-id="element-355" data-mp="2i3v2c5fvafwy">
            That page has moved on.
          </h1>
          <Button to="/shop" className="mt-8" data-id="element-356" data-mp="2rupol9t45fie">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-357" data-mp="2mwxxgi0alxf9" />
            Back to the shop
          </Button>
        </div>
      </main>;
  }
  const items = [...productsInCategory(category.slug)].sort((a, b) => a.name.localeCompare(b.name));
  const others = categories.filter(item => item.slug !== category.slug);
  return <main data-id="element-358" data-mp="287rx2qppy15s">
      <section className="bg-cream" data-id="element-359" data-mp="288a5smtki7ul">
        <div className="mx-auto max-w-shell px-6 pb-14 pt-8 md:px-8 md:pb-16 md:pt-12" data-id="element-360" data-mp="289cv4qb8s28e">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm" data-id="element-361" data-mp="244vc4tcphsyo">
            <Link to="/shop" className="text-ink-muted transition-colors hover:text-ink" data-id="element-362" data-mp="3jm5mi004eonx">
              Shop
            </Link>
            <span className="text-ink-muted" data-id="element-363" data-mp="3flbxf3axz3tg">/</span>
            <span className="text-ink" data-id="element-364" data-mp="2cb7lhk1rcp5t">{category.name}</span>
          </nav>

          <div className="mt-8 grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-16" data-id="element-365" data-mp="2h9si79hswqpi">
            <div data-id="element-366" data-mp="2m8vo0w5lv0yj">
              <h1 className="font-serif text-4xl font-semibold leading-[1.05] text-ink md:text-6xl" data-id="element-367" data-mp="2m8c9lpf5yup9">
                {category.name}
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink-soft" data-id="element-368" data-mp="2q8cwlygbwe7x">
                {category.description}
              </p>
              <ul className="mt-8 flex flex-wrap gap-x-8 gap-y-3" data-id="element-369" data-mp="1yirmqvfw3dap">
                {category.outcomes.map(outcome => <li key={outcome} className="flex items-center gap-2 text-sm text-ink" data-id="element-370" data-mp="22jtn5fljlve9">
                    <CheckIcon className="h-4 w-4 shrink-0 text-brick" strokeWidth={2.25} data-id="element-371" data-mp="27buucn3usvxf" />
                    {outcome}
                  </li>)}
              </ul>
              <Button to="/get-started" className="mt-8" data-id="element-372" data-mp="1ev1z90ezjz7a">
                Get started
              </Button>
            </div>
            <img src={category.image} alt={`${category.name} protocols`} className="aspect-[4/3] w-full rounded-brand bg-cream-deep object-cover" data-id="element-373" data-mp="17aq41do0ko96" />
          </div>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-374" data-mp="ycxlm0mzx0hk">
        <div className="mx-auto max-w-shell px-6 py-14 md:px-8 md:py-16" data-id="element-375" data-mp="tct2s3ym7to6">
          <div className="flex flex-wrap items-baseline justify-between gap-3" data-id="element-376" data-mp="ocn8sh4vhv2x">
            <h2 className="font-serif text-2xl font-semibold text-ink" data-id="element-377" data-mp="oernlu4b8ehz">
              {items.length} treatments in {category.name}
            </h2>
            <Link to="/shop" className="text-sm text-brick transition-colors hover:text-ink" data-id="element-378" data-mp="eo5aq7nza22z">
              See everything
            </Link>
          </div>
          <ul className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-id="element-379" data-mp="5mfkv69qvb7b">
            {items.map(product => <ProductCard key={product.slug} product={product} data-id="element-380" data-mp="n2mppdfahpf" />)}
          </ul>
        </div>
      </section>

      <section className="bg-cream" data-id="element-381" data-mp="20qwsf5yes97t">
        <div className="mx-auto max-w-shell px-6 py-14 md:px-8 md:py-16" data-id="element-382" data-mp="20ry67i6t79t8">
          <h2 className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-383" data-mp="25nvm5ne5ue5h">
            Also worth a look
          </h2>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-id="element-384" data-mp="1vyvktxc6r6o4">
            {others.map(item => <li key={item.slug} data-id="element-385" data-mp="1hzcbx2bjvrar">
                <Link to={`/categories/${item.slug}`} className="block h-full rounded-brand border border-ink/10 bg-cream-light p-5 transition-colors duration-150 ease-republic hover:border-ink/40" data-id="element-386" data-mp="1hyb18yyg56tk">
                  <span className="block font-serif text-base font-semibold text-ink" data-id="element-387" data-mp="1czzsysnsv4dg">
                    {item.name}
                  </span>
                  <span className="mt-1 block text-sm text-ink-muted" data-id="element-388" data-mp="32g8yqg6yxz7r">{item.tagline}</span>
                </Link>
              </li>)}
          </ul>
        </div>
      </section>

      <CtaBand data-id="element-389" data-mp="vpvs9gn6xfw3" />
    </main>;
}